import React, { useMemo, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import TalentsApiService from "../../TalentsApiService";
import { t, lookups } from "../../content";
import { countCompletedRequirements, instrumentIds } from "../../Utilities";
import { useLocale } from "../../locale";

import PageLayout from "../../components/layoutComponents/PageLayout";
import Loading from "../../components/reusableElementsComponents/Loading";
import OnboardingStepper from "../../components/layoutComponents/navigation/OnboardingStepper";
import MazhrModal from "../../components/reusableElementsComponents/MazhrModal";
import { SalesSelect } from "../../components/formComponents/Inputs";

import {
  FormControlLabel,
  Checkbox,
  Typography,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from "@mui/material";
import assessmentCompletedCheckmark from "../../assets/images/icons/assessmentCompletedCheckmark.png";
import assessmentCardBackgroundPlaceholder from "../../assets/images/backgrounds/assessmentCardBackground-102.png";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AssessmentIcon from '@mui/icons-material/Assessment';
import RedoIcon from '@mui/icons-material/Redo';
import {Circle as CircleIcon} from "@mui/icons-material";

function instructionItems(instruction) {
  if (Array.isArray(instruction)) return instruction;
  if (!instruction) return [];
  return String(instruction)
    .split("-")
    .map((item) => item.trim())
    .filter(Boolean);
}

function RegistrationStage3() {
  const { locale } = useLocale();
  const [requiredAssessments, setRequiredAssessments] = useState([]);
  const [completedAssessments, setCompletedAssessments] = useState([]);
  const [assessmentToBeRetaken, setAssessmentToBeRetaken] = useState(null);
  const [openRetakeDialog, setOpenRetakeDialog] = useState(false);
  const [languageChoice, setLanguageChoice] = useState(null);
  const [rememberChoice, setRememberChoice] = useState(false);

  const defaultAssessments = lookups.assessmentCards;

  const userCompletedAssessments =
    useSelector((state) => state.userData?.all?.completed_assessments) || null;

  const poolRequiredAssessments =
      useSelector((state) => state.poolData?.requiredAssessments) || null;

  const teamRequiredAssessments =
    useSelector((state) => state.teamData?.requiredAssessments) || null;

  const allAssessmentsCompleted =
    useSelector((state) => state.userData?.allAssessmentsCompleted) || false;

  useEffect(() => {
    setLanguageChoice((current) => {
      const code = current?.code ?? 2;
      return lookups.testLanguages.find((item) => item.code === code) || lookups.testLanguages[0];
    });
  }, [locale]);

  const talentsServiceObj = useMemo(() => {
    return new TalentsApiService();
  }, []);

  useEffect(() => {
    const nextRequired = instrumentIds(
      (poolRequiredAssessments || []).concat(teamRequiredAssessments || [])
    );

    if (nextRequired?.length >= 0) {
      setRequiredAssessments(nextRequired);
    }

    if (userCompletedAssessments?.length >= 0) {
      setCompletedAssessments(userCompletedAssessments);
    }

  }, [poolRequiredAssessments, teamRequiredAssessments, userCompletedAssessments]);

  const handleStartTest = (providerName, instrumentId, languageId) => {
    talentsServiceObj
      .GetAssessmentUrl(
        providerName || "aon",
        instrumentId || 102,
        languageId || 2
      )
      .then((res) => {
        if (res.httpStatus === 200 && res.json && res.json.data) {
          const url = res.json.data?.url;
          if (url && url.includes("https://", 0)) {
            window.location.assign(url);
          } else {
            console.log("GetAssessmentUrl url is not valid  >> ", url);
          }
        } else {
          console.log("GetAssessmentUrl error  >> ", res);
        }
      });
  };

  const retakeItems = instructionItems(assessmentToBeRetaken?.instruction);

  return (
    <div className="bg-cover" key={locale}>
      <PageLayout>
        <div className="assessment-page-wrapper">
          <h1>{t("assessments.listTitle")}</h1>
        </div>
      </PageLayout>
      <PageLayout>
        {defaultAssessments?.length > 0 && userCompletedAssessments ? (
          <>
            <OnboardingStepper
              activeStepNumber={teamRequiredAssessments ? 2 : 3}
              isTeamMember={teamRequiredAssessments ? true : false}
              disabled={allAssessmentsCompleted ? true : false}
            />
            <div className="assessment-page-wrapper">
              <h5 className="grey s login-form-text-left">
                {t("assessments.listSubtitle")}
              </h5>
              {renderAssessmentProgress(
                requiredAssessments || [],
                completedAssessments || []
              )}

              {renderAllAssessments(
                defaultAssessments || [],
                requiredAssessments || [],
                completedAssessments || [],
                setAssessmentToBeRetaken,
                setOpenRetakeDialog
              )}
            </div>
          </>
        ) : (
          <Loading sectionLoading containerHeight="50vh" />
        )}
      </PageLayout>

      <MazhrModal
        show={openRetakeDialog}
        onHide={() => setOpenRetakeDialog(false)}
        header={<h3 style={{color: '#C13F8A', textAlign:'left', margin:'0',padding:'0'}}>
          {t("assessments.retake")}
        </h3>}
      >
        <div className="mar-35 left">
          <ul>
            <li>
              <Typography variant="h5" className="test-instruction-heading">
                {t("assessments.instructions")}
              </Typography>
              <List>
                {retakeItems.length > 0
                    ? retakeItems.map((item, index) => (
                        <ListItem key={index}>
                          <ListItemIcon>
                            <CircleIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText primary={item} />
                        </ListItem>
                    ))
                    : (
                        <Typography variant="body2">
                          {t("assessments.noInstructions")}
                        </Typography>
                    )}
              </List>
            </li>
            <li className="mar-15 top">
              <b>{t("assessments.selectLanguage")}</b>
            </li>
            <li className="mar-15 top">
              <SalesSelect
                label=""
                options={lookups.testLanguages}
                handleChange={(e) => {
                  const { value } = e.target;
                  setLanguageChoice(
                      lookups.testLanguages.find((item) => item.title === value)
                  );
                }}
                errorContent={t("validation.pleaseSelectGroup")}
                option={languageChoice?.title}
                inputProps={{ style: {} }}
              />
            </li>
            <li className="mar-15 top">
              <FormControlLabel
                className="test-instruction-select-language"
                control={
                  <Checkbox
                    checked={rememberChoice}
                    name="remeber_language_choice"
                    onChange={(e) => {
                      const { checked } = e.target;
                      setRememberChoice(checked);
                    }}
                  />
                }
                label={<span>{t("assessments.rememberChoice")}</span>}
                sx={{ "& .MuiSvgIcon-root": { fontSize: 20 } }}
              />
            </li>
          </ul>
        </div>
        <Alert severity="warning" style={{backgroundColor: '#FDECEF', color: '#C13F8A'}}>
          <Typography variant="h6" style={{color: '#C13F8A'}}>
            <strong>{t("assessments.retakeWarning")}</strong>
          </Typography>
        </Alert>
        <Divider sx={{marginBottom: 2}}/>
        <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
          <button
            className="mazhrBtn default-green-border"
            onClick={() => setOpenRetakeDialog(false)}
          >
            {t("common.actions.cancel")}
          </button>
          <button
            className="mazhrBtn default-green mar-20 left"
            style={{backgroundColor:'#FFBB31'}}
            onClick={() =>
              assessmentToBeRetaken &&
              handleStartTest(
                "aon",
                assessmentToBeRetaken?.instrumentId,
                languageChoice?.code
              )
            }
          >
            {t("assessments.retake")}
          </button>
        </div>
      </MazhrModal>
    </div>
  );
}

const getCompletionPercentage = (completed = 0, uncompleted = 1) =>
  `${((completed / uncompleted) * 100).toFixed(0)}%`;

const renderAssessmentProgress = (
  requiredAssessments,
  completedAssessments
) => {
  const { requiredNumber: requiredCount, completedNumber } = countCompletedRequirements(
    requiredAssessments,
    completedAssessments
  );
  const requiredNumber = requiredCount > 0 ? requiredCount : null;

  return (
    requiredNumber && (
      <div className="assessment-progress">
        <div>
          <div>
            {t("assessments.progressTaken", {
              completed: completedNumber,
              required: requiredNumber,
            })}{" "}
            <LightTooltip
              placement="top-end"
              title={t("assessments.progressTooltip")}
            >
              <InfoOutlinedIcon className="required-note-icon" />
            </LightTooltip>
          </div>
          <div className="assessment-progress-slider">
            <div className="assessment-progress-bar">
              <div
                className="assessment-progress-bar-fill"
                style={{
                  width: getCompletionPercentage(
                    completedNumber,
                    requiredNumber
                  ),
                }}
              />
            </div>
            <div className="assessment-progress-bar-indicator">
              {getCompletionPercentage(completedNumber, requiredNumber)}
            </div>
          </div>
        </div>
      </div>
    )
  );
};

const renderAllAssessments = (
  defaultAssessments,
  requiredAssessments,
  completedAssessments,
  setAssessmentToBeRetaken,
  setOpenRetakeDialog
) => {
  let categories = [];
  let assessmentsToBeRendered = [];

  defaultAssessments
    .filter((x) => requiredAssessments.some((id) => Number(id) === Number(x.instrumentId)))
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .forEach((x) => {
      const completed = completedAssessments.some(
        (y) => Number(y) === Number(x.instrumentId)
      );

      if (x.category && !categories.includes(x.category)) {
        categories.push(x.category);
      }
      assessmentsToBeRendered.push({
        ...x,
        completed,
      });
    });

  return categories.map((category, index) => {
    return (
      <div key={index} className="mar-25 bottom">
        <h3>{category}</h3>
        <div className="assessment-card-wrapper">
          {assessmentsToBeRendered.map((card) => {
            if (card.category === category) {
              return (
                <div className="assessment-card" key={card.instrumentId}>
                  <div
                    className={`assessment-card-header card-background-${card.instrumentId}`}
                    style={{
                      backgroundImage: `url(${
                        card.backgroundImages[0] ||
                        assessmentCardBackgroundPlaceholder
                      })`,
                    }}
                  >
                    <h3>{card.titleDesktop}</h3>
                    <p>{card.subtitleDesktop}</p>
                    {card.completed ? (
                      <div className="assessment-card-notification-wrapper">
                        <img
                          className="assessment-card-completed-icon"
                          src={assessmentCompletedCheckmark}
                          alt="background"
                        />
                      </div>
                    ) : 
                    (
                      <LightTooltip
                        placement="top-end"
                        title={t("assessments.requiredTooltip")}
                      >
                        <div className="assessment-card-notification-wrapper required-note">
                          <InfoOutlinedIcon /> {t("assessments.required")}
                        </div>
                      </LightTooltip>
                    ) 
                    }
                  </div>
                    {card.completed ? (
                        <div className="assessment-completed-card-body">
                          <div className="assessment-card-cta-wrapper">
                            <p>
                              <Link
                                className="m bold underline"
                                to={card.pageUrlPath}
                              >
                                {t("assessments.viewResults")}
                                &nbsp;<AssessmentIcon/>
                              </Link>
                            </p>
                            <p>
                              <Link
                                className="m bold underline"
                                onClick={() => {
                                  setAssessmentToBeRetaken(card);
                                  setOpenRetakeDialog(true);
                                }}
                              >

                                {t("assessments.retakeTest")}
                                &nbsp;<RedoIcon/>
                              </Link>
                            </p>
                          </div>
                        </div>
                    ) : (
                      <>
                      <div className="assessment-card-body">
                        <p>{card.shortDescription}</p>
                        <div className="assessment-card-cta-wrapper">
                          <Link
                            className="mazhrBtn default-green white"
                            to={card.pageUrlPath}
                          >
                            {t("assessments.doThisAssessment")}
                            {" >>"}
                          </Link>
                        </div>
                      </div>
                      </>
                    )}
                </div>
              );
            } else {
              return null;
            }
          })}
        </div>
      </div>
    );
  });
};

const LightTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: "rgba(0, 0, 0, 0.87)",
    boxShadow: theme.shadows[1],
    border: "1px solid red",
    fontSize: 14,
  },
}));

export default RegistrationStage3;
